import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Bell, X, Users, Calendar, DollarSign, FileText, RefreshCw } from "lucide-react";
import { InputVenture } from "@/components/ui/input-venture";
import { supabase } from "@/integrations/supabase/client";
import { useRole, isAdmin, isClient, isTeam } from "@/contexts/RoleContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { HelpButton } from "@/components/layout/HelpButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TopBarProps {
  breadcrumbs: string[];
}

interface SearchResult {
  id: string;
  type: 'client' | 'demo' | 'billing' | 'team';
  title: string;
  subtitle: string;
  path: string;
}

export function TopBar({ breadcrumbs }: TopBarProps) {
  const [initials, setInitials] = useState("VH");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const { role, clientId } = useRole();
  const navigate = useNavigate();

  // Get user initials
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const accountType = user.user_metadata?.account_type;
      let name = '';

      if (accountType === 'admin' || accountType === 'team') {
        // Admins AND team members (setter/closer) resolve their display name from admin_users.
        // Previously `accountType !== 'admin'` implicit-binary forced team users into the
        // clients lookup, which always returned null and fell back to the email prefix.
        const { data: admin } = await supabase
          .from('admin_users')
          .select('full_name')
          .eq('email', user.email)
          .single();
        name = admin?.full_name || '';
      } else {
        const { data: client } = await supabase
          .from('clients')
          .select('contact_name')
          .eq('email', user.email)
          .single();
        name = client?.contact_name || '';
      }

      if (!name) name = user.email || 'VH';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        setInitials((parts[0][0] + parts[1][0]).toUpperCase());
      } else if (name.length >= 2) {
        setInitials(name.substring(0, 2).toUpperCase());
      }
    };
    getUser();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input = searchRef.current?.querySelector('input');
        input?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const performSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);

    const searchResults: SearchResult[] = [];
    const term = `%${q}%`;

    try {
      if (isAdmin(role)) {
        // Admin: search clients, demos, team members
        const [clientsRes, demosRes, teamRes] = await Promise.all([
          supabase.from('clients').select('id, company_name, contact_name, email, status')
            .or(`company_name.ilike.${term},contact_name.ilike.${term},email.ilike.${term}`)
            .limit(5),
          supabase.from('demos').select('id, prospect_name, status, demo_date, client_id')
            .ilike('prospect_name', term)
            .limit(5),
          supabase.from('admin_users').select('id, full_name, email, role')
            .or(`full_name.ilike.${term},email.ilike.${term}`)
            .limit(3),
        ]);

        if (clientsRes.data) {
          for (const c of clientsRes.data) {
            searchResults.push({
              id: c.id,
              type: 'client',
              title: c.company_name,
              subtitle: `${c.contact_name || ''} — ${c.email} (${c.status})`,
              path: '/admin/command-center',
            });
          }
        }

        if (demosRes.data) {
          for (const d of demosRes.data) {
            searchResults.push({
              id: d.id,
              type: 'demo',
              title: d.prospect_name || 'Demo',
              subtitle: `${d.status} — ${d.demo_date || 'No date'}`,
              path: '/admin/demos-downloads',
            });
          }
        }

        if (teamRes.data) {
          for (const t of teamRes.data) {
            searchResults.push({
              id: t.id,
              type: 'team',
              title: t.full_name,
              subtitle: `${t.email} — ${t.role}`,
              path: '/admin/roles-permissions',
            });
          }
        }
      } else if (isClient(role) && clientId) {
        // Client: search only their own demos (RLS enforced).
        // Team (setter/closer) roles skip the search entirely — they have no clientId
        // scope and the admin clients-search would be a privilege bump. Leaving the
        // result list empty is the correct behavior until team-scoped search lands.
        const demosRes = await supabase
          .from('demos')
          .select('id, prospect_name, status, demo_date')
          .eq('client_id', clientId)
          .ilike('prospect_name', term)
          .limit(8);

        if (demosRes.data) {
          for (const d of demosRes.data) {
            searchResults.push({
              id: d.id,
              type: 'demo',
              title: d.prospect_name || 'Demo',
              subtitle: `${d.status} — ${d.demo_date || 'No date'}`,
              path: '/demos-downloads',
            });
          }
        }
      }
    } catch (err) {
      console.error('Search error:', err);
    }

    setResults(searchResults);
    setSearching(false);
  }, [role, clientId]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setQuery(value);
    setIsOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value), 300);
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleGlobalSync = async () => {
    if (syncing) return;
    setSyncing(true);
    const results = await Promise.allSettled([
      supabase.functions.invoke('ghl-sync', { body: { source: 'manual' } }),
      supabase.functions.invoke('google-ads-sync', { body: { source: 'manual' } }),
      supabase.functions.invoke('meta-sync', { body: { source: 'manual' } }),
    ]);
    setSyncing(false);
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      toast.success(`All ${succeeded} integrations synced successfully`);
    } else if (succeeded === 0) {
      toast.error(`All ${failed} integrations failed to sync`);
    } else {
      toast.warning(`${succeeded} synced, ${failed} failed`);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'client': return <Users className="w-4 h-4 text-secondary" />;
      case 'demo': return <Calendar className="w-4 h-4 text-blue-400" />;
      case 'billing': return <DollarSign className="w-4 h-4 text-green-400" />;
      case 'team': return <FileText className="w-4 h-4 text-purple-400" />;
      default: return <Search className="w-4 h-4" />;
    }
  };

  return (
    <TooltipProvider>
    <header className="h-16 border-b border-border bg-background/80 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-40">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm">
        {breadcrumbs.map((crumb, index) => (
          <span key={crumb} className="flex items-center gap-2">
            <span className={index === breadcrumbs.length - 1 ? "text-foreground" : "text-muted-foreground"}>
              {crumb}
            </span>
            {index < breadcrumbs.length - 1 && (
              <span className="text-muted-foreground">/</span>
            )}
          </span>
        ))}
      </div>

      {/* Search */}
      <div className="flex-1 max-w-md mx-8 relative" ref={searchRef}>
        <div className="relative">
          <InputVenture
            icon={<Search className="w-4 h-4" />}
            placeholder="Search..."
            className="h-9 bg-surface"
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => { if (query.length >= 2) setIsOpen(true); }}
          />
          {query ? (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => { setQuery(''); setResults([]); setIsOpen(false); }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-muted-foreground">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">⌘</kbd>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">K</kbd>
            </div>
          )}
        </div>

        {/* Search Results Dropdown */}
        {isOpen && query.length >= 2 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            {searching ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Searching...</div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">No results for "{query}"</div>
            ) : (
              <div className="py-1">
                {results.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-accent transition-colors text-left"
                    onClick={() => handleResultClick(result)}
                  >
                    {getIcon(result.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground uppercase bg-muted px-1.5 py-0.5 rounded">
                      {result.type}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {isAdmin(role) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="p-2 rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                onClick={handleGlobalSync}
                disabled={syncing}
              >
                <RefreshCw className={`w-5 h-5 text-muted-foreground ${syncing ? 'animate-spin' : ''}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent>Sync all integrations</TooltipContent>
          </Tooltip>
        )}
        <HelpButton />
        <div className="relative">
          <button
            data-notif-trigger
            className="relative p-2 rounded-lg hover:bg-accent transition-colors"
            onClick={() => setNotifOpen((prev) => !prev)}
          >
            <Bell className="w-5 h-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <NotificationsDropdown
              isOpen={notifOpen}
              onClose={() => setNotifOpen(false)}
              onUnreadCountChange={setUnreadCount}
            />
          )}
        </div>

        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center cursor-pointer hover:scale-105 transition-transform">
          <span className="text-xs font-bold text-primary-foreground">{initials}</span>
        </div>
      </div>
    </header>
    </TooltipProvider>
  );
}
